import { useMemo } from 'react'
import CompanyRow from '@/components/companies/table/company-row'
import TableFooter from '@/components/companies/table/table-footer'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { filterCompanies } from '@/lib/companies'
import { cn } from '@/lib/utils'
import { useCrmStore } from '@/stores/crm-store-provider'

import {
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table'

import {
    TABLE_CELL_CLASS,
    TABLE_COLUMNS,
    TABLE_GRID_CLASS,
    TABLE_ROW_CLASS
} from '@/components/companies/table/table-columns'

export default function CompaniesTable() {
    const companies = useCrmStore(state => state.companies)
    const status = useCrmStore(state => state.status)
    const sortBy = useCrmStore(state => state.sortBy)
    const owner = useCrmStore(state => state.owner)
    const stage = useCrmStore(state => state.stage)
    const activityWindow = useCrmStore(state => state.activityWindow)
    const selectedIds = useCrmStore(state => state.selectedIds)
    const detailId = useCrmStore(state => state.detailId)
    const detailOpen = useCrmStore(state => state.detailOpen)
    const toggleSelected = useCrmStore(state => state.toggleSelected)
    const setSelected = useCrmStore(state => state.setSelected)
    const openDetail = useCrmStore(state => state.openDetail)
    const openProfile = useCrmStore(state => state.openProfile)

    const visible = useMemo(
        () => filterCompanies(companies, { sortBy, owner, stage, activityWindow }),
        [companies, sortBy, owner, stage, activityWindow]
    )

    const selectedVisible = visible.filter(company => selectedIds.includes(company.id))
    const allSelected = visible.length > 0 && selectedVisible.length === visible.length
    const someSelected = selectedVisible.length > 0 && !allSelected

    function toggleAll() {
        setSelected(allSelected ? [] : visible.map(company => company.id))
    }

    const emptyMessage = status === 'loading'
        ? "Loading companies…"
        : status === 'error'
            ? "Companies couldn't be loaded right now. Refresh the page to try again."
            : companies.length === 0
                ? "No companies yet. Add your first one with New Company."
                : "No companies match the current filters."

    return (
        <div className='border-border flex min-h-0 flex-1 flex-col border-t'>
            <ScrollArea orientation='both' className='min-h-0 flex-1'>
                <Table role='table' className={cn(TABLE_GRID_CLASS, 'w-full')}>
                    <TableHeader role='rowgroup' className='contents'>
                        <TableRow role='row' className={TABLE_ROW_CLASS}>
                            {TABLE_COLUMNS.map(column => (
                                <TableHead
                                    key={column.key}
                                    role='columnheader'
                                    className={cn(TABLE_CELL_CLASS, column.className)}
                                >
                                    {column.key === 'name' ? (
                                        <span className='flex items-center gap-5'>
                                            <Checkbox
                                                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                                                onCheckedChange={toggleAll}
                                                aria-label="Select all companies"
                                            />
                                            {column.label}
                                        </span>
                                    ) : column.label}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody role='rowgroup' className='contents'>
                        {visible.map(company => (
                            <CompanyRow
                                key={company.id}
                                company={company}
                                selected={selectedIds.includes(company.id)}
                                active={detailOpen && detailId === company.id}
                                onToggle={() => toggleSelected(company.id)}
                                onOpen={() => openDetail(company.id)}
                                onOpenOwner={() => openProfile(company.owner)}
                            />
                        ))}
                        {visible.length === 0 && (
                            <TableRow role='row' className={TABLE_ROW_CLASS}>
                                <td
                                    role='cell'
                                    className='caption-style text-muted-foreground col-span-full flex h-[120px] items-center justify-center px-4 text-center'
                                >
                                    {emptyMessage}
                                </td>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </ScrollArea>
            <TableFooter count={visible.length} />
        </div>
    )
}
